
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { logger } from '@/utils/logger';
import { captureException } from '@/observability/posthog';
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Uncaught error:', error, errorInfo);
    captureException(error, { componentStack: errorInfo.componentStack });
    this.setState({ errorInfo });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private getErrorMessage = (): string => {
    if (!this.state.error) {
      return 'Unknown error occurred';
    }

    let errorMessage = this.state.error.toString();
    
    if (this.state.errorInfo?.componentStack) {
      errorMessage += '\n\nComponent Stack:' + this.state.errorInfo.componentStack;
    }

    return errorMessage;
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="dark flex flex-col items-center justify-center min-h-screen p-8 bg-background text-foreground">
          <div className="text-center max-w-md">
            <AlertTriangle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            <div className="space-y-3">
              <Button onClick={this.handleRetry} className="w-full">
                Try Again
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.reload()} 
                className="w-full"
              >
                Refresh Page
              </Button>
            </div>
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Error Details
              </summary>
              <pre className="mt-2 text-xs text-muted-foreground bg-card border border-border p-3 rounded-md overflow-auto max-h-40 whitespace-pre-wrap">
                {this.getErrorMessage()}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
